import { useRef } from 'react';
import { queryString } from '@olivierpascal/helpers';
import { Button, CopyableText, Divider, Stack, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';

import type { IContainerProps } from '~/components/utils/Container';
import { Link } from '~/components/atoms/Link';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { getAppSettings } from '~/lib/appSettings';

export type ISmartLinkProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr min-content 1fr',
    paddingInline: 32,
    columnGap: 32,
  },
  badge: {
    height: 56,
  },
  title: {
    paddingBlock: 32,
  },
  title$left: {
    textAlign: 'center',
  },
  title$right: {
    textAlign: 'center',
  },
  panel: {
    paddingBottom: 32,
  },
  panel$left: {},
  panel$right: {},
  qrCodeContainer: {
    borderRadius: shapeTokens.corner$lg,
    overflow: 'hidden',
    borderWidth: outlineTokens.width$xs,
    borderStyle: 'solid',
    borderColor: colorSchemeTokens.outlineVariant,
  },
});

export const SmartLink: React.FC<ISmartLinkProps> = (props) => {
  const { sx, appId } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const appQuery = useAppQuery({ appId });
  const appSettings = getAppSettings();

  const iosBundleId = appQuery.data?.builds?.ios?.ready
    ? appQuery.data?.builds?.ios?.iosAppId
    : undefined;
  const appStoreUrl = iosBundleId
    ? `https://apps.apple.com/fr/app/freeform/id${iosBundleId}`
    : undefined;

  const androidPackageId = appQuery.data?.builds?.android?.ready
    ? appQuery.data?.builds?.android?.packageId
    : undefined;
  const googlePlayUrl = androidPackageId
    ? `https://play.google.com/store/apps/details?id=${androidPackageId}`
    : undefined;

  const qrCodeRef = useRef<QRCode>(null);
  const hasLink = appStoreUrl || googlePlayUrl;
  const smartLinkUrl = `${appSettings.environment.url}/nosw/r.v1/?${queryString.build(
    {
      i: iosBundleId,
      a: androidPackageId,
    },
  )}`;

  const handleDownloadAsPng = (): void => {
    qrCodeRef.current?.download('png', `${appQuery.data?.name}.png`);
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
        first
      >
        <ContentBlock.Body extended={!!hasLink} first={!hasLink}>
          {hasLink ? (
            <div {...stylex.props(styles.container)}>
              <Stack gap={2} sx={[styles.title, styles.title$left]}>
                <Text variant="title" size="md">
                  {t('SmartLink.regularLink.title')}
                </Text>
                <Text variant="body" size="md" dimmed>
                  {t('SmartLink.regularLink.subtitle')}
                </Text>
              </Stack>
              <Divider orientation="vertical" />
              <Stack gap={2} sx={[styles.title, styles.title$right]}>
                <Text variant="title" size="md">
                  {t('SmartLink.smartLink.title')}
                </Text>
                <Text variant="body" size="md" dimmed>
                  {t('SmartLink.smartLink.subtitle')}
                </Text>
              </Stack>

              <Stack
                sx={[styles.panel, styles.panel$left]}
                gap={6}
                align="center"
                justify="center"
              >
                {appStoreUrl ? (
                  <Stack gap={2} align="center">
                    <Link to={appStoreUrl} target="_blank" noExternalIcon>
                      <img
                        {...stylex.props(styles.badge)}
                        src={`/static/images/${locale}/appStoreBadge.black.svg`}
                        alt={t('SmartLink.downloadOnAppStore')}
                      />
                    </Link>
                    <CopyableText
                      text={appStoreUrl}
                      copySupportingText={t('SmartLink.copyLink')}
                    >
                      {appStoreUrl}
                    </CopyableText>
                  </Stack>
                ) : null}
                {googlePlayUrl ? (
                  <Stack gap={2} align="center">
                    <Link to={googlePlayUrl} target="_blank" noExternalIcon>
                      <img
                        {...stylex.props(styles.badge)}
                        src={`/static/images/${locale}/googlePlayBadge.png`}
                        alt={t('SmartLink.getItOnGooglePlay')}
                      />
                    </Link>
                    <CopyableText
                      text={googlePlayUrl}
                      copySupportingText={t('SmartLink.copyLink')}
                    >
                      {googlePlayUrl}
                    </CopyableText>
                  </Stack>
                ) : null}
              </Stack>
              <Divider orientation="vertical" />
              <Stack
                sx={[styles.panel, styles.panel$right]}
                gap={4}
                align="center"
                justify="center"
              >
                <Stack align="center" gap={2}>
                  <div {...stylex.props(styles.qrCodeContainer)}>
                    <a href={smartLinkUrl} target="_blank" rel="noreferrer">
                      <QRCode
                        ref={qrCodeRef}
                        value={smartLinkUrl}
                        size={240}
                        quietZone={16}
                        // logoImage={appQuery.data?.icon?.mediumUrl}
                        // fgColor={
                        //   appQuery.data?.settings?.press?.env?.appThemeColorPrimary
                        // }
                        // logoPadding={64}
                        // logoPaddingStyle='circle'
                        removeQrCodeBehindLogo
                      />
                    </a>
                  </div>
                  <Stack gap={2} align="center">
                    <Button onClick={handleDownloadAsPng} variant="text">
                      {t('SmartLink.downloadQrCode')}
                    </Button>
                    <CopyableText
                      text={smartLinkUrl}
                      copySupportingText={t('SmartLink.copyLink')}
                    >
                      {smartLinkUrl}
                    </CopyableText>
                  </Stack>
                </Stack>
              </Stack>
            </div>
          ) : (
            t('SmartLink.notAvailable')
          )}
        </ContentBlock.Body>
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
